<template>
  <teleport to="body">
    <div class="affiliate-referral-overlay" @click.self="close">
      <div class="affiliate-referral-modal" role="dialog" aria-labelledby="new-referral-title">
        <div class="affiliate-referral-modal__head">
          <h3 id="new-referral-title">New referral</h3>
          <p>Two quick steps. Whatever you create lands on your dashboard.</p>
        </div>

        <div class="affiliate-referral-steps" :class="{ 'is-share': step === 'share' }">
          <div class="affiliate-referral-step" :class="{ 'is-active': step === 'split', 'is-done': step === 'share' }">
            <span class="affiliate-referral-step__num">1</span>
            Choose split
          </div>
          <div class="affiliate-referral-step" :class="{ 'is-active': step === 'share' }">
            <span class="affiliate-referral-step__num">2</span>
            Share
          </div>
        </div>

        <template v-if="step === 'split'">
          <div class="affiliate-referral-split-label">
            Pick a split
            <span class="affiliate-referral-info" title="Your share plus their discount always adds up to 10%.">i</span>
          </div>

          <div class="affiliate-referral-presets">
            <button
              v-for="preset in presets"
              :key="preset.id"
              type="button"
              class="affiliate-referral-preset"
              :class="{ active: selectedPreset === preset.id }"
              @click="selectPreset(preset.id)"
            >
              <div class="affiliate-referral-preset__title">{{ preset.title }}</div>
              <div class="affiliate-referral-preset__desc">{{ preset.desc }}</div>
              <div class="affiliate-referral-preset__split">
                <span class="is-you">You {{ preset.youLabel }}</span>
                <span class="is-they">They {{ preset.theyLabel }}</span>
              </div>
            </button>
          </div>

          <div class="affiliate-referral-fields">
            <label>
              Your share %
              <input
                v-model="commissionInput"
                type="text"
                inputmode="decimal"
                class="affiliate-referral-share-input"
                :class="{
                  'is-editable': selectedPreset === 'custom',
                  'is-invalid': !!commissionError,
                }"
                :readonly="selectedPreset !== 'custom'"
                @keydown="validateInput"
                @input="onCommissionInput"
              />
              <span v-if="commissionError" class="affiliate-referral-error">{{ commissionError }}</span>
            </label>
            <label>
              Their discount %
              <input
                :value="discountDisplay"
                type="text"
                readonly
                :class="{ 'is-invalid': !!discountError }"
              />
              <span v-if="selectedPreset === 'custom' && !discountError" class="affiliate-referral-autofill">
                Auto-filled: 10% minus your share.
              </span>
              <span v-if="discountError" class="affiliate-referral-error">{{ discountError }}</span>
            </label>
          </div>

          <p v-if="selectedPreset === 'custom'" class="affiliate-referral-max-share">
            Max share is 10% – your share + their discount can't exceed 10%.
          </p>
          <p class="affiliate-referral-example">
            On a $1,000 order you’d earn <strong>{{ earnPreview }}</strong>.
            Referral links earn on <strong>every order they place</strong>.
            Referral codes earn only when the code is used at checkout.
          </p>
        </template>

        <template v-else>
          <div class="affiliate-referral-split-label">How do you want to share it? <span class="affiliate-referral-pick">(pick one)</span></div>
          <div class="affiliate-share-methods">
            <button
              type="button"
              class="affiliate-share-method"
              :class="{ active: shareMethod === 'link' }"
              @click="shareMethod = 'link'"
            >
              <span class="affiliate-share-method__check" v-if="shareMethod === 'link'"></span>
              <span class="affiliate-share-method__icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                  <path d="M10 13a5 5 0 007.07 0l1.41-1.41a5 5 0 00-7.07-7.07L10 5.93" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                  <path d="M14 11a5 5 0 00-7.07 0L5.52 12.41a5 5 0 007.07 7.07L14 18.07" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                </svg>
              </span>
              <span class="affiliate-share-method__name">Referral link</span>
              <span class="affiliate-share-method__meta">For new signups</span>
            </button>
            <button
              type="button"
              class="affiliate-share-method"
              :class="{ active: shareMethod === 'code' }"
              @click="shareMethod = 'code'"
            >
              <span class="affiliate-share-method__check" v-if="shareMethod === 'code'"></span>
              <span class="affiliate-share-method__icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                  <rect x="3.5" y="6.5" width="17" height="11" rx="2.5" stroke="currentColor" stroke-width="1.8"/>
                  <path d="M7 10h4M7 14h7" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                </svg>
              </span>
              <span class="affiliate-share-method__name">Referral Code</span>
              <span class="affiliate-share-method__meta">For checkout</span>
            </button>
          </div>

          <div v-if="shareMethod === 'link'" class="affiliate-referral-fields">
            <label>
              Link label <span class="affiliate-referral-pick">(only you see this)</span>
              <input v-model="linkLabel" type="text" class="is-editable" placeholder="Spring partners" />
            </label>
            <label>
              Your link <span class="affiliate-referral-pick">(preview)</span>
              <div class="affiliate-link-preview" :class="{ 'is-invalid': !!linkError }">
                <span>{{ linkHost }}/</span><strong>{{ encodedReferral }}</strong>
              </div>
              <span v-if="linkError" class="affiliate-referral-error">{{ linkError }}</span>
            </label>
          </div>
          <p v-if="shareMethod === 'link'" class="affiliate-share-help">
            Send it to someone who doesn’t have an account yet. When they sign up through it, every order they place gets <strong>{{ formatPercent(discount) }} off</strong> and you earn <strong>{{ formatPercent(commission) }}</strong>.
          </p>

          <div v-if="shareMethod === 'code'" class="affiliate-referral-fields">
            <label>
              Referral Code label <span class="affiliate-referral-pick">(only you see this)</span>
              <input v-model="codeLabel" type="text" class="is-editable" placeholder="Podcast promo" />
            </label>
            <label>
              Referral Code
              <div class="affiliate-code-input">
                <input
                  v-model="customCode"
                  type="text"
                  class="is-code is-editable"
                  :class="{ 'is-invalid': !!codeError }"
                  maxlength="15"
                  placeholder="SUMMER10"
                  @keydown="validateCodeKey"
                  @input="onCustomCodeInput"
                />
                <span v-if="codeChecking" class="affiliate-code-spinner"></span>
              </div>
              <span v-if="codeError" class="affiliate-referral-error">{{ codeError }}</span>
            </label>
          </div>
          <p v-if="shareMethod === 'code'" class="affiliate-share-help">
            Create your own code. They enter it on the checkout screen; that order gets <strong>{{ formatPercent(discount) }} off</strong> and you earn <strong>{{ formatPercent(commission) }}</strong>.
          </p>
          <span v-if="shareError" class="affiliate-referral-error">{{ shareError }}</span>
        </template>

        <div class="affiliate-referral-footer">
          <button type="button" class="affiliate-referral-cancel" @click="close">Cancel</button>
          <div class="affiliate-referral-footer__right">
            <button type="button" class="affiliate-referral-back" :disabled="step === 'split'" @click="goBack">
              Back
            </button>
            <button
              v-if="step === 'split'"
              type="button"
              class="affiliate-referral-continue"
              :disabled="!canContinue"
              @click="goToShare"
            >
              Continue
            </button>
            <button
              v-else
              type="button"
              class="affiliate-referral-continue"
              :disabled="!canCreate || submitting"
              @click="createReferral"
            >
              Create
            </button>
          </div>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script>
import { numberFormat } from "@/prod.js";
import url from "@/prod.js";
import { isReservedReferralSlug, reservedReferralKeywordMessage } from "@/utils/affiliateRef";

const PRESETS = [
  { id: "generous", title: "Generous", desc: "Best for winning a hesitant contact.", you: 3, they: 7 },
  { id: "balanced", title: "Balanced", desc: "Even split. Most common.", you: 5, they: 5 },
  { id: "max", title: "Max earn", desc: "Keep most of it.", you: 7, they: 3 },
  { id: "custom", title: "Custom", desc: "Any whole number from 0 to 10.", you: null, they: null },
];

function slugFromLabel(label) {
  return String(label || "")
    .trim()
    .replace(/\s+/g, "-")
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "")
    .replace(/-+/g, "-")
    .replace(/^[_-]+|[_-]+$/g, "");
}

export default {
  name: "NewReferralModal",
  emits: ["close", "created"],
  data() {
    return {
      presets: PRESETS.map((preset) => ({
        ...preset,
        youLabel: preset.you === null ? "?" : `${preset.you}%`,
        theyLabel: preset.they === null ? "?" : `${preset.they}%`,
      })),
      step: "split",
      selectedPreset: "balanced",
      commission: 5,
      discount: 5,
      commissionInput: "5",
      errors: {},
      submitting: false,
      shareMethod: "link",
      linkLabel: "",
      codeLabel: "",
      customCode: "",
      codeStatus: "",
      codeError: "",
      codeChecking: false,
      codeCheckTimer: null,
      shareError: "",
      linkError: "",
      linkChecking: false,
      linkCheckTimer: null,
    };
  },
  computed: {
    normalizedCustomCode() {
      return String(this.customCode || "")
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, "");
    },
    discountDisplay() {
      if (this.discount === "" || this.discount === null || Number.isNaN(Number(this.discount))) {
        return "";
      }
      return String(this.discount);
    },
    earnPreview() {
      const share = Number(this.commission);
      if (!Number.isFinite(share)) return "$0";
      return numberFormat((1000 * share) / 100, "currency");
    },
    commissionError() {
      if (this.errors.commission && this.errors.commission[0]) return this.errors.commission[0];
      if (this.commission === "" || this.commission === null) return "";
      const share = Number(this.commission);
      if (!Number.isFinite(share)) return "The commission must be a number.";
      if (share > 10) return "The commission may not be greater than 10.";
      if (share < 0) return "The commission must be at least 0.";
      return "";
    },
    discountError() {
      if (this.errors.discount && this.errors.discount[0]) return this.errors.discount[0];
      if (this.discount === "" || this.discount === null) return "";
      const discount = Number(this.discount);
      if (!Number.isFinite(discount)) return "The discount must be a number.";
      if (discount < 0) return "The discount must be at least 0.";
      if (discount > 10) return "The discount may not be greater than 10.";
      return "";
    },
    canContinue() {
      const share = Number(this.commission);
      const discount = Number(this.discount);
      if (!Number.isFinite(share) || !Number.isFinite(discount)) return false;
      if (this.commissionError || this.discountError) return false;
      return Math.abs(share + discount - 10) < 0.001;
    },
    linkHost() {
      return String(url.appUrl || window.location.origin)
        .replace(/^https?:\/\//, "")
        .replace(/\/$/, "");
    },
    encodedReferral() {
      return slugFromLabel(this.linkLabel);
    },
    usesLinkRef() {
      return this.shareMethod === "link";
    },
    canCreate() {
      if (this.shareMethod === "link") {
        return (
          String(this.linkLabel || "").trim().length > 0 &&
          this.encodedReferral.length > 0 &&
          !this.linkError &&
          !this.linkChecking
        );
      }
      if (this.shareMethod === "code") {
        return (
          String(this.codeLabel || "").trim().length > 0 &&
          this.normalizedCustomCode.length >= 4 &&
          this.normalizedCustomCode.length <= 15 &&
          !this.codeError &&
          !this.codeChecking &&
          this.codeStatus === "available"
        );
      }
      return false;
    },
    usesReferralCode() {
      return this.shareMethod === "code";
    },
  },
  watch: {
    encodedReferral() {
      this.scheduleReferralCheck();
    },
    usesLinkRef() {
      this.scheduleReferralCheck();
    },
    usesReferralCode(needed) {
      if (needed) this.scheduleCodeCheck();
    },
  },
  beforeUnmount() {
    clearTimeout(this.linkCheckTimer);
    clearTimeout(this.codeCheckTimer);
  },
  methods: {
    formatPercent(value) {
      const number = Number(value);
      if (!Number.isFinite(number)) return "0%";
      return `${number % 1 === 0 ? number : number.toFixed(2)}%`;
    },
    close() {
      this.$emit("close");
    },
    goToShare() {
      if (!this.canContinue) return;
      this.shareError = "";
      this.linkError = "";
      this.codeError = "";
      this.step = "share";
      this.$nextTick(() => this.scheduleReferralCheck());
    },
    goBack() {
      this.step = "split";
      this.shareError = "";
    },
    selectPreset(id) {
      this.selectedPreset = id;
      this.errors = {};
      const preset = PRESETS.find((item) => item.id === id);
      if (preset && preset.you !== null) {
        this.setSplit(preset.you);
      }
    },
    setSplit(share) {
      const commission = this.roundValue(Number(share));
      this.commission = commission;
      this.discount = this.roundValue(10 - commission);
      this.commissionInput = String(this.commission);
    },
    roundValue(value) {
      const number = Number(value);
      if (!Number.isFinite(number)) return "";
      if (Number.isInteger(number)) return number;
      return Number(number.toFixed(2));
    },
    onCommissionInput() {
      if (this.selectedPreset !== "custom") return;
      this.errors = {};
      const raw = String(this.commissionInput || "").trim();
      if (raw === "" || raw === "-") {
        this.commission = "";
        this.discount = "";
        return;
      }
      const share = Number(raw);
      if (!Number.isFinite(share)) return;
      this.commission = this.roundValue(share);
      this.discount = this.roundValue(10 - share);
    },
    validateInput(event) {
      const char = event.key;
      const input = event.target.value;
      if (this.selectedPreset !== "custom") {
        event.preventDefault();
        return;
      }
      if (
        !/^\d$/.test(char) &&
        char !== "." &&
        char !== "Backspace" &&
        char !== "ArrowLeft" &&
        char !== "ArrowRight" &&
        char !== "Delete" &&
        char !== "Tab"
      ) {
        event.preventDefault();
      }
      if (char === "." && input.includes(".")) {
        event.preventDefault();
      }
      if (char === "." && input === "") {
        event.preventDefault();
      }
      const decimalPart = input.split(".")[1];
      if (
        decimalPart &&
        decimalPart.length >= 2 &&
        !["Backspace", "Delete", "ArrowLeft", "ArrowRight"].includes(char)
      ) {
        event.preventDefault();
      }
    },
    scheduleReferralCheck() {
      clearTimeout(this.linkCheckTimer);
      this.linkError = "";
      if (!this.usesLinkRef || !this.encodedReferral) {
        this.linkChecking = false;
        return;
      }
      if (isReservedReferralSlug(this.encodedReferral)) {
        this.linkChecking = false;
        this.linkError = reservedReferralKeywordMessage();
        return;
      }
      this.linkChecking = true;
      this.linkCheckTimer = setTimeout(() => {
        this.checkReferralAvailability();
      }, 400);
    },
    async checkReferralAvailability() {
      const label = String(this.linkLabel || "").trim();
      const slug = this.encodedReferral;
      if (!this.usesLinkRef || !slug) {
        this.linkChecking = false;
        this.linkError = "";
        return;
      }
      if (isReservedReferralSlug(slug)) {
        this.linkChecking = false;
        this.linkError = reservedReferralKeywordMessage();
        return;
      }
      this.linkChecking = true;
      try {
        const res = await this.$store.dispatch("AgencyUsers/checkAffiliateCodeDataAction", {
          type: "link",
          label,
          code: slug,
        });
        if (label !== String(this.linkLabel || "").trim()) return;
        this.linkError = res && res.available === false ? "Not available" : "";
      } catch (error) {
        if (label !== String(this.linkLabel || "").trim()) return;
        this.linkError = "";
      } finally {
        if (label === String(this.linkLabel || "").trim()) this.linkChecking = false;
      }
    },
    validateCodeKey(event) {
      const char = event.key;
      if (
        !/^[a-zA-Z0-9]$/.test(char) &&
        char !== "Backspace" &&
        char !== "ArrowLeft" &&
        char !== "ArrowRight" &&
        char !== "Delete" &&
        char !== "Tab"
      ) {
        event.preventDefault();
      }
    },
    onCustomCodeInput() {
      this.customCode = this.normalizedCustomCode;
      this.scheduleCodeCheck();
    },
    scheduleCodeCheck() {
      clearTimeout(this.codeCheckTimer);
      this.codeError = "";
      this.codeStatus = "";
      if (!this.usesReferralCode) {
        this.codeChecking = false;
        return;
      }
      const code = this.normalizedCustomCode;
      if (!code) {
        this.codeChecking = false;
        return;
      }
      if (code.length < 4 || code.length > 15) {
        this.codeChecking = false;
        this.codeError = "Code must be 4 to 15 characters.";
        return;
      }
      this.codeChecking = true;
      this.codeCheckTimer = setTimeout(() => {
        this.checkCustomCodeAvailability();
      }, 400);
    },
    async checkCustomCodeAvailability() {
      const code = this.normalizedCustomCode;
      if (!this.usesReferralCode || !code || code.length < 4 || code.length > 15) {
        this.codeChecking = false;
        return;
      }
      this.codeChecking = true;
      try {
        const res = await this.$store.dispatch("AgencyUsers/checkAffiliateCodeDataAction", {
          type: "code",
          code,
        });
        if (code !== this.normalizedCustomCode) return;
        if (res && res.available === false) {
          this.codeStatus = "taken";
          this.codeError = "This code already exists. Try a different code.";
        } else {
          this.codeStatus = "available";
          this.codeError = "";
        }
      } catch (error) {
        if (code !== this.normalizedCustomCode) return;
        this.codeStatus = "";
        this.codeError = "";
      } finally {
        if (code === this.normalizedCustomCode) this.codeChecking = false;
      }
    },
    async confirmCodeAvailableOnCreate() {
      clearTimeout(this.codeCheckTimer);
      const code = this.normalizedCustomCode;
      if (!code || code.length < 4 || code.length > 15) {
        this.codeStatus = "";
        this.codeError = "Code must be 4 to 15 characters.";
        return false;
      }
      this.codeChecking = true;
      try {
        const res = await this.$store.dispatch("AgencyUsers/checkAffiliateCodeDataAction", {
          type: "code",
          code,
        });
        if (code !== this.normalizedCustomCode) return false;
        if (res && res.available === false) {
          this.codeStatus = "taken";
          this.codeError = "This code already exists. Try a different code.";
          return false;
        }
        this.codeStatus = "available";
        this.codeError = "";
        return true;
      } catch (error) {
        if (code !== this.normalizedCustomCode) return false;
        return true;
      } finally {
        if (code === this.normalizedCustomCode) this.codeChecking = false;
      }
    },
    async confirmLinkAvailableOnCreate() {
      clearTimeout(this.linkCheckTimer);
      const label = String(this.linkLabel || "").trim();
      const slug = this.encodedReferral;
      if (!label || !slug) {
        this.linkError = "Not available";
        return false;
      }
      if (isReservedReferralSlug(slug)) {
        this.linkError = reservedReferralKeywordMessage();
        return false;
      }
      this.linkChecking = true;
      try {
        const res = await this.$store.dispatch("AgencyUsers/checkAffiliateCodeDataAction", {
          type: "link",
          label,
          code: slug,
        });
        if (label !== String(this.linkLabel || "").trim()) return false;
        if (res && res.available === false) {
          this.linkError = "Not available";
          return false;
        }
        this.linkError = "";
        return true;
      } catch (error) {
        if (label !== String(this.linkLabel || "").trim()) return false;
        return true;
      } finally {
        if (label === String(this.linkLabel || "").trim()) this.linkChecking = false;
      }
    },
    async createReferral() {
      if (!this.canCreate || this.submitting) return;
      this.shareError = "";
      this.linkError = "";
      this.submitting = true;
      if (this.usesLinkRef) {
        const stillAvailable = await this.confirmLinkAvailableOnCreate();
        if (!stillAvailable) {
          this.submitting = false;
          return;
        }
      } else if (this.usesReferralCode) {
        const stillAvailable = await this.confirmCodeAvailableOnCreate();
        if (!stillAvailable) {
          this.submitting = false;
          return;
        }
      }
      const createdType = this.usesReferralCode ? "code" : "link";
      const label = this.usesReferralCode
        ? String(this.codeLabel).trim()
        : String(this.linkLabel).trim();
      const payload = {
        commission: this.commission,
        discount: this.discount,
        referral_url: this.usesReferralCode ? this.normalizedCustomCode : this.encodedReferral,
        type: createdType,
        label,
        code: this.normalizedCustomCode,
      };
      try {
        const res = await this.$store.dispatch("AgencyUsers/createAffiliateUrlDataAction", payload);
        if (res.success) {
          this.$emit(
            "created",
            res.message || (createdType === "code" ? "Referral code created" : "Affiliate URL Created")
          );
          this.close();
        } else {
          const message = res.error || "Unable to create referral";
          if (this.usesLinkRef && /keyword is not available/i.test(message)) {
            this.linkError = reservedReferralKeywordMessage();
          } else if (this.usesLinkRef && /not available/i.test(message)) {
            this.linkError = "Not available";
          } else if (this.usesReferralCode && /already exists|already taken/i.test(message)) {
            this.codeStatus = "taken";
            this.codeError = "This code already exists. Try a different code.";
          } else {
            this.shareError = message;
          }
        }
      } catch (error) {
        const errors = error.response?.data?.errors;
        const message =
          error.response?.data?.error ||
          (errors && (errors.code?.[0] || errors.label?.[0])) ||
          "Unable to create referral";
        if (this.usesLinkRef && /keyword is not available/i.test(message)) {
          this.linkError = reservedReferralKeywordMessage();
        } else if (this.usesLinkRef && /not available/i.test(message)) {
          this.linkError = "Not available";
        } else if (this.usesReferralCode && /already exists|already taken/i.test(message)) {
          this.codeStatus = "taken";
          this.codeError = "This code already exists. Try a different code.";
        } else {
          this.shareError = message;
        }
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>
